import { Button, Input, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import ArchiveIcon from "../../../assets/icons/ArchiveIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import UpdateAnyStatus from "../../../components/admin/Forum/anyStatus/UpdateAnyStatus";
import CampaignView from "../../../components/admin/ManageEvents/CampaignView";
import FilterSection from "../../../components/common/FilterSection";
import ActionButton from "../../../components/ui/ActionButton";
import CustomTable from "../../../components/ui/CustomTable";
import useDebounce from "../../../hooks/useDebounce";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import { getAllCampaignAsync } from "../../../store/features/EventManage/Campaigns/CampaignApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { TableData } from "../../../types";
import { GetCampaignData } from "../../../types/eventManage/campaigns";
import { countRow } from "../../../utils/number";
import { addKeyInArray, changeStatus, skipData } from "../../../utils/utils";
import UpdateAnyStatusCampaign from "../../../components/admin/Forum/anyStatus/UpdateAnyStatusCampaign";

type Status =
  | "Published"
  | "CSO-Admin-Approved"
  | "Cancel"
  | "Completed"
  | "Pending";

const dealerStatusClass: Record<Status, string> = {
  Published: "status-waiting",
  "CSO-Admin-Approved": "status-waiting",
  Cancel: "status-danger",
  Completed: "status-done",
  Pending: "status-pending",
};

const changeStatused = (status: Status) => {
  // Optional formatter (e.g., replace hyphens with spaces and capitalize)
  return status.replace(/-/g, " ");
};

export default function Campaigns() {
  const dispatch = useAppDispatch();
  const { keyword, handleInputChange } = useDebounce();
  const { open, toggle, handleOpen } = useModal(reRender);
  const { limit, page, handlePagination, handleLimit, resetPagination } =
    useTable();
  const { CampaignList, countCampaign, loading } = useAppSelector(
    (state) => state.campaigns
  );

  const columns: ColumnsType<TableData<GetCampaignData>> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, idx) => countRow(page, limit, idx),
    },
    {
      title: "Campaign Title",
      dataIndex: "campaignTitle",
      render: (val) => <span>{val || "N/A"}</span>,
    },

    {
      title: "Address",
      dataIndex: "campaignAddress",
      render: (val) => <span>{val || "N/A"}</span>,
    },
    {
      title: "Organized By",
      dataIndex: "campaignOrganizedBy",
      render: (val) => <span>{val || "N/A"}</span>,
    },
    {
      title: "Host By",
      dataIndex: "campaignHostBy",
      render: (val) => <span>{val || "N/A"}</span>,
    },

    {
      title: "Start Date",
      dataIndex: "campaignStartDate",
      render: (val) => (
        <div className="">
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "End Date",
      dataIndex: "campaignEndDate",
      render: (val) => (
        <div className="">
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "campaignStatus",
      render: (val: Status) => (
        <span className={dealerStatusClass[val]}>{changeStatused(val)}</span>
      ),
    },

    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val) => (
        <Space size="small">
          <ActionButton
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val,
              })
            }
          />

          <Link
            to={`/admin/event-manage/campaigns/add-campaigns/${val?.campaignId}`}
          >
            <ActionButton
              icon={<EditIcon />}
              onClick={() =>
                handleOpen({
                  type: "EDIT",
                  data: val,
                })
              }
            />
          </Link>

          <ActionButton
            icon={<ArchiveIcon />}
            onClick={() =>
              handleOpen({
                type: "ARCHIVE",
                data: val,
              })
            }
          />
        </Space>
      ),
    },
  ];

  const getData = useCallback(() => {
    dispatch(
      getAllCampaignAsync({
        params: {
          skip: skipData(page, limit),
          limit,
          custom_text: keyword,
          platform: "admin",
          userId: "",
        },
      })
    );
  }, [dispatch, limit, page, keyword]);

  useEffect(() => {
    getData();
  }, [getData]);

  function reRender() {
    getData();
  }

  return (
    <AdminLayout
      title="Campaign List"
      className="dashboard"
      headerChildren={
        <Link to={`/admin/event-manage/campaigns/add-campaigns`}>
          <Button
            className="primary_btn"
            type="primary"
            size="large"
            icon={<AddIcon />}
            onClick={() =>
              handleOpen({
                type: "ADD",
              })
            }
          >
            Create New Campaign
          </Button>
        </Link>
      }
    >
      <CustomTable<TableData<GetCampaignData>>
        loading={loading}
        total={countCampaign}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        handleLimit={handleLimit}
        dataList={addKeyInArray(CampaignList)}
        columns={columns}
        bordered={false}
        isLimitRow
        showTotalPage
        tableTopChildren={
          <FilterSection>
            <Input
              size="large"
              suffix={<SearchIcon />}
              placeholder={`Search within ${countCampaign} data`}
              onChange={(e) => {
                handleInputChange(e);
                resetPagination();
              }}
            />
          </FilterSection>
        }
      />

      {open?.type === "VIEW" && (
        <CampaignView open={open} handleClose={toggle} />
      )}

      {open?.type === "ARCHIVE" && (
        <UpdateAnyStatusCampaign
          open={{
            type: "EDIT",
            open: true,
            data: {
              id: open.data?.campaignId,
              type: "campaign",
              event_type: "campaign",
              status: "Archived",
            },
          }}
          handleClose={toggle}
        />
      )}
    </AdminLayout>
  );
}
